import { Link, useLocation } from "react-router-dom";
import { useIdentityStatus } from "@/lib/api/hooks";
import { identityMessage } from "@/types/identity";

/** Informational only: never intercepts purchases, appointments, or navigation. */
export function IdentityStatusBanner({
  suppressed = false,
}: {
  suppressed?: boolean;
}) {
  const identity = useIdentityStatus();
  const { pathname } = useLocation();
  const focusedFlow =
    /^\/(?:app\/(?:intake|shop|marketplace|upgrade|treatment-offers)|onboarding)(?:\/|$)/.test(
      pathname,
    );
  if (
    suppressed ||
    focusedFlow ||
    !identity.data?.enabled ||
    identity.data.status === "on_file"
  )
    return null;
  const { title, body } = identityMessage(identity.data.status);
  const needsUpload =
    identity.data.status === "missing" ||
    identity.data.status === "correction_required";
  return (
    <section
      aria-label="Identity verification"
      className="m-15 border border-line bg-sunken p-15"
    >
      <h2 className="type-label-md">{title}</h2>
      <p className="type-body-sm mt-6 text-ink-secondary">{body}</p>
      {identity.data.reason && (
        <p className="type-body-sm mt-6">{identity.data.reason}</p>
      )}
      <p className="type-body-sm mt-6 text-ink-muted">
        Your appointments remain available. Billing is separate from
        prescription authorization.
      </p>
      {identity.isError && (
        <p role="alert" className="type-body-sm mt-6 text-danger">
          We could not refresh your identity status.{" "}
          <button
            type="button"
            className="underline"
            onClick={() => identity.refetch()}
          >
            Retry
          </button>
        </p>
      )}
      {needsUpload && (
        <Link
          className="inline-block mt-10 underline type-label-sm"
          to="/app/clinic?tab=documents&upload=id"
        >
          {identity.data.status === "correction_required"
            ? "Update ID"
            : "Upload ID"}
        </Link>
      )}
    </section>
  );
}
